import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { RefreshCw, FolderPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PeriodFilter } from '../components/PeriodFilter';

import { FinancialSummaryCards } from '../components/financials/FinancialSummaryCards';
import { CashAccountsTab } from '../components/financials/CashAccountsTab';
import { ReceivablesTab } from '../components/financials/ReceivablesTab';
import { PayablesTab } from '../components/financials/PayablesTab';
import { AssetsTab } from '../components/financials/AssetsTab';
import { LiabilitiesTab } from '../components/financials/LiabilitiesTab';
import { CapitalTab } from '../components/financials/CapitalTab';
import { TransfersTab } from '../components/financials/TransfersTab';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: 'cash', label: 'Cash Accounts' },
  { id: 'receivables', label: 'Receivables' },
  { id: 'assets', label: 'Other Assets' },
  { id: 'payables', label: 'Payables' },
  { id: 'liabilities', label: 'Other Liabilities' },
  { id: 'capital', label: 'Capital' },
  { id: 'transfers', label: 'Transfers' }
];

const FinancialManagementPage = () => {
  const { hasPermission, isAdmin } = useAuth();
  const canDeleteAsset = hasPermission('delete_asset');
  const canDeleteLiability = hasPermission('delete_liability');
  const canDeleteTransfer = hasPermission('delete_transfer');
  const canDeleteCapital = hasPermission('delete_capital');

  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [capital, setCapital] = useState([]);
  const [equitySummary, setEquitySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountsSummary, setAccountsSummary] = useState(null);
  const [receivables, setReceivables] = useState({ items: [], total_receivables: 0 });
  const [payables, setPayables] = useState({ items: [], total_payables: 0 });
  const [activeTab, setActiveTab] = useState('cash');
  const [recalculating, setRecalculating] = useState(false);
  const [periodFilter, setPeriodFilter] = useState({ startDate: '', endDate: '', label: '' });

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryType, setCategoryType] = useState('asset');
  const [newCategory, setNewCategory] = useState('');
  const [assetCategories, setAssetCategories] = useState(['MoMo', 'Equity Bank', 'Bank of Kigali', 'Cash', 'Inventory', 'Equipment', 'Receivables']);
  const [liabilityCategories, setLiabilityCategories] = useState(['Accounts Payable', 'Loans', 'Other']);

  useEffect(() => {
    fetchData();
    const savedAssetCategories = localStorage.getItem('assetCategories');
    const savedLiabilityCategories = localStorage.getItem('liabilityCategories');
    if (savedAssetCategories) setAssetCategories(JSON.parse(savedAssetCategories));
    if (savedLiabilityCategories) setLiabilityCategories(JSON.parse(savedLiabilityCategories));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter]);

  const fetchData = useCallback(async () => {
    try {
      const dateParams = new URLSearchParams();
      if (periodFilter.startDate) dateParams.append('start_date', periodFilter.startDate);
      if (periodFilter.endDate) dateParams.append('end_date', periodFilter.endDate);
      const qs = dateParams.toString() ? `?${dateParams.toString()}` : '';

      const [assetsRes, liabilitiesRes, transfersRes, capitalRes, equityRes, accountsRes, receivablesRes, payablesRes] = await Promise.all([
        axios.get(`${API_URL}/assets`),
        axios.get(`${API_URL}/liabilities`),
        axios.get(`${API_URL}/transfers`),
        axios.get(`${API_URL}/capital`),
        axios.get(`${API_URL}/equity-summary`),
        axios.get(`${API_URL}/accounts/full-summary${qs}`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/accounts/receivables`).catch(() => ({ data: { items: [], total_receivables: 0 } })),
        axios.get(`${API_URL}/accounts/payables`).catch(() => ({ data: { items: [], total_payables: 0 } }))
      ]);
      setAssets(assetsRes.data);
      setLiabilities(liabilitiesRes.data);
      setTransfers(transfersRes.data);
      setCapital(capitalRes.data);
      setEquitySummary(equityRes.data);
      setAccountsSummary(accountsRes.data);
      setReceivables(receivablesRes.data);
      setPayables(payablesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [periodFilter]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await axios.post(`${API_URL}/accounts/recalculate`);
      toast.success('Account balances recalculated');
      fetchData();
    } catch (error) {
      toast.error('Failed to recalculate balances');
    } finally {
      setRecalculating(false);
    }
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    if (categoryType === 'asset') {
      if (assetCategories.includes(newCategory.trim())) {
        toast.error('This category already exists');
        return;
      }
      const updated = [...assetCategories, newCategory.trim()];
      setAssetCategories(updated);
      localStorage.setItem('assetCategories', JSON.stringify(updated));
    } else {
      if (liabilityCategories.includes(newCategory.trim())) {
        toast.error('This category already exists');
        return;
      }
      const updated = [...liabilityCategories, newCategory.trim()];
      setLiabilityCategories(updated);
      localStorage.setItem('liabilityCategories', JSON.stringify(updated));
    }
    toast.success('Category added successfully');
    setNewCategory('');
    setCategoryDialogOpen(false);
  };

  // Computed totals
  const totalAssets = assets.reduce((sum, a) => sum + a.amount, 0);
  const totalLiabilities = liabilities.reduce((sum, l) => sum + l.amount, 0);
  const totalCapital = capital.reduce((sum, c) => sum + c.amount, 0);
  const retainedEarnings = equitySummary?.retained_earnings?.total || 0;
  const totalEquity = totalCapital + retainedEarnings;
  const cashAccountsTotal = accountsSummary?.total_cash || 0;
  const receivablesTotal = receivables.total_receivables || accountsSummary?.total_receivables || 0;
  const payablesTotal = payables.total_payables || accountsSummary?.total_payables || 0;
  const combinedAssets = totalAssets + cashAccountsTotal + receivablesTotal;
  const combinedLiabilities = totalLiabilities + payablesTotal;
  const netWorth = combinedAssets - combinedLiabilities;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="financial-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Financial Management</h1>
          <p className="text-muted-foreground mt-1">Track cash accounts, receivables, payables, assets, and liabilities</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button
                onClick={handleRecalculate}
                disabled={recalculating}
                variant="outline"
                className="gap-2"
                data-testid="recalculate-btn"
              >
                <RefreshCw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
                Recalculate
              </Button>
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-category-btn">
                    <FolderPlus className="w-4 h-4 mr-2" />Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add New Category</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Category Type *</label>
                      <Select value={categoryType} onValueChange={setCategoryType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asset">Asset Category</SelectItem>
                          <SelectItem value="liability">Liability Category</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Category Name *</label>
                      <Input
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="e.g., Petty Cash, Supplier Credit"
                        data-testid="new-category-input"
                      />
                    </div>
                    <Button onClick={handleAddCategory} className="w-full" data-testid="save-category-btn">
                      Add Category
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Period Filter */}
      <PeriodFilter onFilterChange={setPeriodFilter} />

      {/* Summary Cards */}
      <FinancialSummaryCards
        cashAccountsTotal={cashAccountsTotal}
        receivablesTotal={receivablesTotal}
        totalAssets={totalAssets}
        payablesTotal={payablesTotal}
        totalLiabilities={totalLiabilities}
        netWorth={netWorth}
      />

      {/* Tabs Navigation */}
      <div className="border-b border-border">
        <div className="flex gap-1 overflow-x-auto pb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-6">
        {activeTab === 'cash' && (
          <CashAccountsTab accountsSummary={accountsSummary} cashAccountsTotal={cashAccountsTotal} />
        )}
        {activeTab === 'receivables' && (
          <ReceivablesTab receivables={receivables} receivablesTotal={receivablesTotal} />
        )}
        {activeTab === 'payables' && (
          <PayablesTab payables={payables} payablesTotal={payablesTotal} />
        )}
        {activeTab === 'assets' && (
          <AssetsTab
            assets={assets}
            assetCategories={assetCategories}
            isAdmin={isAdmin}
            canDeleteAsset={canDeleteAsset}
            onRefresh={fetchData}
          />
        )}
        {activeTab === 'liabilities' && (
          <LiabilitiesTab
            liabilities={liabilities}
            liabilityCategories={liabilityCategories}
            isAdmin={isAdmin}
            canDeleteLiability={canDeleteLiability}
            onRefresh={fetchData}
          />
        )}
        {activeTab === 'capital' && (
          <CapitalTab
            capital={capital}
            totalCapital={totalCapital}
            retainedEarnings={retainedEarnings}
            totalEquity={totalEquity}
            isAdmin={isAdmin}
            canDeleteCapital={canDeleteCapital}
            onRefresh={fetchData}
          />
        )}
        {activeTab === 'transfers' && (
          <TransfersTab
            transfers={transfers}
            canDeleteTransfer={canDeleteTransfer}
            onRefresh={fetchData}
          />
        )}
      </div>
    </div>
  );
};

export default FinancialManagementPage;
